// Below this, a pointer gesture is a click on a node rather than a drag of the canvas. Mermaid nodes
// are clickable (the inspector), so a few pixels of movement while pressing must not swallow the
// click — and a real drag must not leave a click behind it. See #onPointerUp.
const DRAG_THRESHOLD_PX = 4;
export class PanZoom {
    constructor(container, content, limits = {}) {
        this.container = container;
        this.content = content;
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.dragging = false;
        this.moved = false;
        this.startPointer = { x: 0, y: 0 };
        this.startOffset = { x: 0, y: 0 };
        this.onPointerDownBound = (e) => this.onPointerDown(e);
        this.onPointerMoveBound = (e) => this.onPointerMove(e);
        this.onPointerUpBound = (e) => this.onPointerUp(e);
        this.onWheelBound = (e) => this.onWheel(e);
        this.onClickCaptureBound = (e) => this.onClickCapture(e);
        this.limits = { min: limits.min ?? 0.4, max: limits.max ?? 3.0 };
        this.content.style.transformOrigin = '0 0';
        // Clipped rather than scrolled: a transform contributes nothing to scroll extent, so `auto` shows
        // a scrollbar that does not reach the scaled content. Dragging is the way around the canvas now.
        this.container.classList.add('pan-zoom-active');
        this.container.addEventListener('pointerdown', this.onPointerDownBound);
        this.container.addEventListener('pointermove', this.onPointerMoveBound);
        this.container.addEventListener('pointerup', this.onPointerUpBound);
        this.container.addEventListener('pointercancel', this.onPointerUpBound);
        this.container.addEventListener('wheel', this.onWheelBound, { passive: false });
        this.container.addEventListener('click', this.onClickCaptureBound, true);
        this.apply();
    }
    destroy() {
        this.container.removeEventListener('pointerdown', this.onPointerDownBound);
        this.container.removeEventListener('pointermove', this.onPointerMoveBound);
        this.container.removeEventListener('pointerup', this.onPointerUpBound);
        this.container.removeEventListener('pointercancel', this.onPointerUpBound);
        this.container.removeEventListener('wheel', this.onWheelBound);
        this.container.removeEventListener('click', this.onClickCaptureBound, true);
        this.container.classList.remove('pan-zoom-active', 'is-pannable', 'is-panning');
    }
    get state() {
        return { scale: this.scale, x: this.x, y: this.y };
    }
    /**
     * @param factor added to the current scale, the way the toolbar buttons have always expressed it
     * @param anchor a point in CONTAINER coordinates to hold still; the container's centre by default,
     *   which is what makes the buttons behave like the old centre-origin zoom
     */
    zoomBy(factor, anchor) {
        this.zoomTo(this.scale + factor, anchor);
    }
    zoomTo(target, anchor) {
        const next = Math.min(this.limits.max, Math.max(this.limits.min, target));
        if (next === this.scale)
            return;
        const rect = this.container.getBoundingClientRect();
        const at = anchor ?? { x: rect.width / 2, y: rect.height / 2 };
        // Keep `at` over the same part of the diagram: its position in content space is
        // (at - offset) / scale, and that must be unchanged after the scale changes.
        this.x = at.x - ((at.x - this.x) * next) / this.scale;
        this.y = at.y - ((at.y - this.y) * next) / this.scale;
        this.scale = next;
        this.clamp();
        this.apply();
    }
    reset() {
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.apply();
    }
    pannable() {
        return this.scale > 1;
    }
    onPointerDown(event) {
        // Left button only, and only once there is something to pan to. A diagram at its natural size
        // fits its container, so dragging it would only move it away from the reader.
        if (event.button !== 0 || !this.pannable())
            return;
        this.dragging = true;
        this.moved = false;
        this.startPointer = { x: event.clientX, y: event.clientY };
        this.startOffset = { x: this.x, y: this.y };
        this.container.classList.add('is-panning');
        // NOT preventDefault: a press that turns out to be a click still has to reach the node under it.
        this.container.setPointerCapture?.(event.pointerId);
    }
    onPointerMove(event) {
        if (!this.dragging)
            return;
        const dx = event.clientX - this.startPointer.x;
        const dy = event.clientY - this.startPointer.y;
        if (!this.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX)
            return;
        this.moved = true;
        this.x = this.startOffset.x + dx;
        this.y = this.startOffset.y + dy;
        this.clamp();
        this.apply();
    }
    onPointerUp(event) {
        if (!this.dragging)
            return;
        this.dragging = false;
        this.container.classList.remove('is-panning');
        this.container.releasePointerCapture?.(event.pointerId);
    }
    // A drag that ends over a node would otherwise open that node's inspector, which is not what
    // someone repositioning the canvas asked for. Swallowed in the capture phase so the handler on the
    // node never runs; `moved` is cleared here rather than on pointerup so this can read it.
    onClickCapture(event) {
        if (!this.moved)
            return;
        this.moved = false;
        event.stopPropagation();
        event.preventDefault();
    }
    // Ctrl/⌘ + wheel, never plain wheel: several of these diagrams sit one after another down a
    // scrolling page, and taking the wheel over would trap the page under the pointer.
    onWheel(event) {
        if (!event.ctrlKey && !event.metaKey)
            return;
        event.preventDefault();
        const rect = this.container.getBoundingClientRect();
        const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        // deltaY is negative scrolling up, which is the direction that should enlarge.
        this.zoomTo(this.scale * (event.deltaY < 0 ? 1.12 : 1 / 1.12), anchor);
    }
    // Keeps the diagram overlapping its container. Without this a drag can throw the whole thing off
    // screen with no way back except Reset, which is a trap rather than a feature.
    clamp() {
        const rect = this.container.getBoundingClientRect();
        const width = this.content.offsetWidth * this.scale;
        const height = this.content.offsetHeight * this.scale;
        this.x = this.axisClamp(this.x, width, rect.width);
        this.y = this.axisClamp(this.y, height, rect.height);
    }
    axisClamp(value, contentSize, containerSize) {
        // Smaller than its container: centred, and not draggable in that axis at all.
        if (contentSize <= containerSize)
            return (containerSize - contentSize) / 2;
        return Math.min(0, Math.max(containerSize - contentSize, value));
    }
    apply() {
        this.content.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
        this.container.classList.toggle('is-pannable', this.pannable());
    }
}
